import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as requirementService from '../services/requirementService';
import type { RequirementWithDetails, CreateRequirementInput } from '../services/requirementService';
import * as commentService from '../services/commentService';
import type { RequirementComment, CreateCommentInput } from '../services/commentService';
import { ChatButton } from '../components/chat/ChatButton';
import { ChatWidget } from '../components/chat/ChatWidget';
import DependencyManagement from '../components/DependencyManagement';
import { marked } from 'marked';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Configure marked to output cleaner HTML
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Helper function to safely parse markdown
const parseMarkdown = (text: string): string => {
  try {
    const result = marked.parse(text);
    return typeof result === 'string' ? result : '';
  } catch (error) {
    console.error('Markdown parsing error:', error);
    return text;
  }
};

// Sortable Requirement Row Component
interface SortableRequirementRowProps {
  req: RequirementWithDetails;
  hasChildren: boolean;
  isExpanded: boolean;
  children: RequirementWithDetails[];
  onToggleCollapse: (id: string) => void;
  onView: (req: RequirementWithDetails) => void;
  onEdit: (req: RequirementWithDetails) => void;
  onApprove: (req: RequirementWithDetails) => void;
  onReject: (req: RequirementWithDetails) => void;
  onDelete: (req: RequirementWithDetails) => void;
  onCollaborate: (req: RequirementWithDetails) => void;
  canApproveReject: boolean;
  getPriorityColor: (priority: string) => string;
  getApprovalStatusColor: (status: string) => string;
  hasActivityToday: (date?: string | null | undefined) => boolean;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
}

function SortableRequirementRow({
  req,
  hasChildren,
  isExpanded,
  children,
  onToggleCollapse,
  onView,
  onEdit,
  onApprove,
  onReject,
  onDelete,
  onCollaborate,
  canApproveReject,
  getPriorityColor,
  getApprovalStatusColor,
  hasActivityToday,
  openDropdownId,
  setOpenDropdownId,
}: SortableRequirementRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: req.id });

  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (openDropdownId === req.id && dropdownButtonRef.current) {
      const buttonRect = dropdownButtonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 400; // Approximate height of dropdown
      const spaceBelow = viewportHeight - buttonRect.bottom;

      // Show above if there's not enough space below and there's more space above
      if (spaceBelow < dropdownHeight && buttonRect.top > spaceBelow) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  }, [openDropdownId, req.id]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <React.Fragment>
      {/* Parent Requirement Row */}
      <div
        ref={setNodeRef}
        style={style}
        className={`bg-surface-primary px-6 py-3 hover:bg-background-secondary transition-colors grid grid-cols-12 gap-4 items-center ${
          isDragging ? 'bg-blue-50 dark:bg-blue-900/30 shadow-lg' : ''
        }`}
      >
        {/* Drag Handle + Title Column */}
        <div className="col-span-2 flex items-start gap-2">
          {/* Drag Handle */}
          <button
            {...attributes}
            {...listeners}
            className="flex-shrink-0 mt-0.5 p-0.5 hover:bg-gray-200 rounded transition-colors cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <svg
              className="h-5 w-5 text-text-tertiary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </button>

          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse(req.id);
              }}
              className="flex-shrink-0 mt-0.5 p-0.5 hover:bg-gray-200 rounded transition-colors"
            >
              <svg
                className={`h-4 w-4 text-text-tertiary transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          <div className={hasChildren ? '' : 'ml-6'}>
            <div className="text-sm font-medium text-text-primary">{req.title}</div>
            {req.description && (
              <div className="text-xs text-text-tertiary mt-1 line-clamp-1">{req.description}</div>
            )}
            {hasChildren && (
              <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mt-1">
                {children.length} {children.length === 1 ? 'child' : 'children'}
              </div>
            )}
          </div>
        </div>
        <div className="col-span-1">
          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPriorityColor(req.priority)}`}>
            {req.priority}
          </span>
        </div>
        <div className="col-span-1">
          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getApprovalStatusColor(req.approval_status)}`}>
            {req.approval_status}
          </span>
        </div>
        <div className="col-span-1">
          {req.expected_end_date ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                {req.is_overdue ? (
                  <svg className="w-3.5 h-3.5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : req.is_on_schedule ? (
                  <svg className="w-3.5 h-3.5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
                <span className="text-xs text-text-secondary">
                  {new Date(req.expected_end_date).toLocaleDateString()}
                </span>
              </div>
              {req.is_overdue ? (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:text-red-400">
                  Overdue
                </span>
              ) : req.is_on_schedule ? (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:text-green-400">
                  On Track
                </span>
              ) : (
                <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:text-yellow-400">
                  At Risk
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs text-text-tertiary">—</span>
          )}
        </div>
        <div className="col-span-2 flex items-center">
          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
            {req.created_by_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
          </div>
          <span className="ml-2 text-sm text-text-secondary">{req.created_by_name}</span>
        </div>
        <div className="col-span-1 flex items-center justify-center">
          {req.insights && (
            <div className="relative group">
              <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                AI Insight Available
              </div>
            </div>
          )}
        </div>
        <div className="col-span-2 flex items-center justify-center gap-4">
          <div className="relative group flex items-center gap-1.5">
            <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-sm font-medium text-text-secondary">{req.comment_count || 0}</span>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
              {req.comment_count || 0} {(req.comment_count || 0) === 1 ? 'Comment' : 'Comments'}
            </div>
          </div>
          <div className="relative group flex items-center gap-1.5">
            <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            <span className="text-sm font-medium text-text-secondary">{req.reply_count || 0}</span>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
              {req.reply_count || 0} {(req.reply_count || 0) === 1 ? 'Reply' : 'Replies'}
            </div>
          </div>
          {hasActivityToday(req.latest_comment_date) && (
            <div className="relative group">
              <svg className="w-4 h-4 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                Activity today
              </div>
            </div>
          )}
        </div>
        <div className="col-span-2 flex items-center justify-end">
          <div className="relative">
            <button
              ref={dropdownButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                setOpenDropdownId(openDropdownId === req.id ? null : req.id);
              }}
              className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-background-tertiary rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            {/* Dropdown Menu */}
            {openDropdownId === req.id && (() => {
              console.log('🔵 Dropdown rendering for req.id:', req.id, 'position:', dropdownPosition);
              return (
              <div
                onClick={(e) => {
                  console.log('Dropdown container clicked!');
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  console.log('Dropdown container mousedown!');
                  e.stopPropagation();
                }}
                style={{ zIndex: 9999, pointerEvents: 'auto' }}
                className={`absolute right-0 w-48 max-h-96 overflow-y-auto bg-white rounded-md shadow-lg border border-border-primary py-1 ${
                  dropdownPosition === 'above' ? 'bottom-full mb-2' : 'mt-2'
                }`}
              >
                <button
                  onClick={(e) => {
                    console.log('View button clicked!');
                    e.stopPropagation();
                    onView(req);
                    setOpenDropdownId(null);
                  }}
                  onMouseDown={(e) => {
                    console.log('View button mousedown!');
                    e.stopPropagation();
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-indigo-50 hover:text-indigo-600 dark:text-indigo-400 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(req);
                    setOpenDropdownId(null);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-blue-50 hover:text-blue-600 dark:text-blue-400 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCollaborate(req);
                    setOpenDropdownId(null);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-indigo-50 hover:text-indigo-600 dark:text-indigo-400 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                  </svg>
                  Collaborate
                </button>
                {canApproveReject && req.approval_status !== 'approved' && (
                  <>
                    <div className="border-t border-border-primary my-1"></div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove(req);
                        setOpenDropdownId(null);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-green-50 hover:text-green-600 dark:text-green-400 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Approve
                    </button>
                  </>
                )}
                {canApproveReject && req.approval_status !== 'rejected' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onReject(req);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-red-50 hover:text-red-600 dark:text-red-400 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                )}

                {/* Separator */}
                {canApproveReject && <div className="border-t border-border-primary my-1"></div>}

                {/* Delete */}
                <button
                  onClick={(e) => {
                    console.log('Delete button clicked in portal!');
                    e.stopPropagation();
                    onDelete(req);
                    setOpenDropdownId(null);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Child Requirements (shown when parent is expanded) */}
      {hasChildren && isExpanded && children.map((child) => (
        <div
          key={child.id}
          className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors grid grid-cols-12 gap-4 items-center border-l-4 border-indigo-300 dark:border-indigo-600"
        >
          <div className="col-span-2 pl-10">
            <div className="flex items-center gap-2">
              <svg className="w-3 h-3 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <div className="text-sm font-medium text-text-primary">{child.title}</div>
            </div>
            {child.description && (
              <div className="text-xs text-text-tertiary mt-1 pl-5 line-clamp-1">{child.description}</div>
            )}
          </div>
          <div className="col-span-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPriorityColor(child.priority)}`}>
              {child.priority}
            </span>
          </div>
          <div className="col-span-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getApprovalStatusColor(child.approval_status)}`}>
              {child.approval_status}
            </span>
          </div>
          <div className="col-span-1">
            {child.expected_end_date ? (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1">
                  {child.is_overdue ? (
                    <svg className="w-3.5 h-3.5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : child.is_on_schedule ? (
                    <svg className="w-3.5 h-3.5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  <span className="text-xs text-text-secondary">
                    {new Date(child.expected_end_date).toLocaleDateString()}
                  </span>
                </div>
                {child.is_overdue ? (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:text-red-400">
                    Overdue
                  </span>
                ) : child.is_on_schedule ? (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:text-green-400">
                    On Track
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:text-yellow-400">
                    At Risk
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs text-text-tertiary">—</span>
            )}
          </div>
          <div className="col-span-2 flex items-center">
            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
              {child.created_by_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>
            <span className="ml-2 text-sm text-text-secondary">{child.created_by_name}</span>
          </div>
          <div className="col-span-1 flex items-center justify-center">
            {child.insights && (
              <div className="relative group">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap pointer-events-none z-50">
                  AI Insight Available
                </div>
              </div>
            )}
          </div>
          <div className="col-span-2 flex items-center justify-center gap-4">
            <div className="relative group flex items-center gap-1.5">
              <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm font-medium text-text-secondary">{child.comment_count || 0}</span>
            </div>
            <div className="relative group flex items-center gap-1.5">
              <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <span className="text-sm font-medium text-text-secondary">{child.reply_count || 0}</span>
            </div>
          </div>
          <div className="col-span-2 flex items-center justify-end">
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDropdownId(openDropdownId === child.id ? null : child.id);
                }}
                className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-background-tertiary rounded transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {openDropdownId === child.id && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute right-0 mt-2 w-48 max-h-96 overflow-y-auto bg-surface-primary rounded-md shadow-lg border border-border-primary py-1 z-50"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onView(child);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-indigo-50 hover:text-indigo-600 dark:text-indigo-400 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(child);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-blue-50 hover:text-blue-600 dark:text-blue-400 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCollaborate(child);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-indigo-50 hover:text-indigo-600 dark:text-indigo-400 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                    </svg>
                    Collaborate
                  </button>

                  {/* Separator */}
                  <div className="border-t border-border-primary my-1"></div>

                  {/* Delete */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(child);
                      setOpenDropdownId(null);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </React.Fragment>
  );
}

export function Requirements(): React.ReactElement | null {
  const { projectId } = useParams<{ projectId: string }>();
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();

  const [requirements, setRequirements] = useState<RequirementWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Confirmation modal state
  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'reject' | 'delete', requirementId: string, requirementTitle: string } | null>(null);

  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [showOnlyPending, setShowOnlyPending] = useState(false);
  const [showWithComments, setShowWithComments] = useState(false);
  const [scheduleFilter, setScheduleFilter] = useState<'all' | 'overdue' | 'on-track' | 'at-risk' | 'no-date'>('all');

  // Add requirement modal
  const [isAddingRequirement, setIsAddingRequirement] = useState(false);
  const [newRequirement, setNewRequirement] = useState<CreateRequirementInput>({
    title: '',
    description: '',
    requirement_type: 'functional',
    priority: 'medium',
    hierarchy_type: 'parent'
  });
  const [parentRequirements, setParentRequirements] = useState<RequirementWithDetails[]>([]);
  const [addError, setAddError] = useState('');

  // View/Edit requirement modals
  const [viewingRequirement, setViewingRequirement] = useState<RequirementWithDetails | null>(null);
  const [editingRequirement, setEditingRequirement] = useState<RequirementWithDetails | null>(null);
  const [editFormData, setEditFormData] = useState<CreateRequirementInput>({
    title: '',
    description: '',
    requirement_type: 'functional',
    priority: 'medium',
    approval_status: 'pending'
  });
  const [editError, setEditError] = useState('');

  // Collaboration modal state
  const [collaboratingRequirement, setCollaboratingRequirement] = useState<RequirementWithDetails | null>(null);
  const [comments, setComments] = useState<RequirementComment[]>([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState<RequirementComment | null>(null);
  const [editingComment, setEditingComment] = useState<RequirementComment | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentError, setCommentError] = useState('');
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  // Chat widget state
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Hierarchy collapse state (with localStorage persistence)
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(() => {
    const saved = localStorage.getItem('requirements_collapsedParents');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  // Quick-add requirement state
  const [quickAddActiveSection, setQuickAddActiveSection] = useState<string | null>(null);
  const [quickAddTitle, setQuickAddTitle] = useState('');

  // Dropdown menu state for actions
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Helper to toggle parent collapse state
  const toggleParentCollapse = (parentId: string) => {
    setCollapsedParents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
      }
      localStorage.setItem('requirements_collapsedParents', JSON.stringify(Array.from(newSet)));
      return newSet;
    });
  };

  // Helper to get children of a parent requirement
  const getChildren = (parentId: string): RequirementWithDetails[] => {
    return requirements.filter(req =>
      req.parent_requirement_id === parentId && req.hierarchy_type === 'child'
    );
  };

  const loadRequirements = async () => {
    if (!token || !projectId) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await requirementService.getProjectRequirements(projectId, token);
      setRequirements(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requirements');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequirements();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, token]);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId) {
        setOpenDropdownId(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openDropdownId]);

  const loadParentRequirements = async () => {
    if (!token || !projectId) return;
    try {
      console.log('Fetching parent requirements for project:', projectId);
      const data = await requirementService.getParentRequirements(projectId, token);
      console.log('Parent requirements received:', data);
      setParentRequirements(data);
    } catch (err) {
      console.error('Failed to load parent requirements:', err);
    }
  };

  const handleAddRequirement = async () => {
    if (!token || !projectId || !newRequirement.title) {
      setAddError('Title is required');
      return;
    }

    // Validate hierarchy: if child, must have parent_requirement_id
    if (newRequirement.hierarchy_type === 'child' && !newRequirement.parent_requirement_id) {
      setAddError('Please select a parent requirement for child requirements');
      return;
    }

    setAddError('');
    try {
      await requirementService.createRequirement(projectId, newRequirement, token);
      setSuccess('Requirement created successfully');
      setIsAddingRequirement(false);
      setNewRequirement({
        title: '',
        description: '',
        requirement_type: 'functional',
        priority: 'medium',
        hierarchy_type: 'parent'
      });
      loadRequirements();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to create requirement');
    }
  };

  const handleOpenAddModal = () => {
    setIsAddingRequirement(true);
    loadParentRequirements();
  };

  // Quick-add requirement handlers
  const handleQuickAddActivate = (requirementType: string) => {
    setQuickAddActiveSection(requirementType);
    setQuickAddTitle('');
  };

  const handleQuickAddCancel = () => {
    setQuickAddActiveSection(null);
    setQuickAddTitle('');
  };

  const handleQuickAddSubmit = async (requirementType: string) => {
    if (!token || !projectId || !quickAddTitle.trim()) {
      return;
    }

    try {
      const quickAddData: CreateRequirementInput = {
        title: quickAddTitle.trim(),
        description: '',
        requirement_type: requirementType as any,
        priority: 'medium',
        hierarchy_type: 'parent'
      };

      await requirementService.createRequirement(projectId, quickAddData, token);
      setSuccess('Requirement created successfully');
      setQuickAddActiveSection(null);
      setQuickAddTitle('');
      loadRequirements();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create requirement');
      setQuickAddActiveSection(null);
      setQuickAddTitle('');
    }
  };

  const handleQuickAddKeyDown = (e: React.KeyboardEvent, requirementType: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuickAddSubmit(requirementType);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleQuickAddCancel();
    }
  };

  // Callback for AI-generated requirements from chatbot
  const handleCreateRequirementFromAI = (requirementData: {
    title: string;
    description: string;
    requirement_type: string;
    priority: string;
  }) => {
    console.log('🎯 handleCreateRequirementFromAI CALLED with data:', requirementData);

    // Pre-populate the newRequirement state with AI data
    setNewRequirement({
      title: requirementData.title,
      description: requirementData.description,
      requirement_type: requirementData.requirement_type as any,
      priority: requirementData.priority as any,
      hierarchy_type: 'parent' // Default to parent for AI-generated requirements
    });

    // Load parent requirements in case user wants to change hierarchy
    loadParentRequirements();

    // Open the modal
    setIsAddingRequirement(true);

    // Optionally close the chat widget to give focus to the modal
    setIsChatOpen(false);
  };

  const handleViewRequirement = (requirement: RequirementWithDetails) => {
    setViewingRequirement(requirement);
  };

  const handleEditRequirement = (requirement: RequirementWithDetails) => {
    setEditingRequirement(requirement);
    setEditFormData({
      title: requirement.title,
      description: requirement.description || '',
      requirement_type: requirement.requirement_type,
      priority: requirement.priority,
      approval_status: requirement.approval_status,
      assigned_to: requirement.assigned_to || undefined,
      expected_start_date: requirement.expected_start_date ? requirement.expected_start_date.split('T')[0] : null,
      expected_end_date: requirement.expected_end_date ? requirement.expected_end_date.split('T')[0] : null
    });
    setEditError('');
  };

  const handleUpdateRequirement = async () => {
    if (!token || !projectId || !editingRequirement || !editFormData.title) {
      setEditError('Title is required');
      return;
    }
    setEditError('');
    try {
      await requirementService.updateRequirement(projectId, editingRequirement.id, editFormData, token);
      setSuccess('Requirement updated successfully');
      setEditingRequirement(null);
      loadRequirements();
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update requirement');
    }
  };

  const handleApproveRequirement = (requirement: RequirementWithDetails) => {
    setConfirmAction({ type: 'approve', requirementId: requirement.id, requirementTitle: requirement.title });
  };

  const handleRejectRequirement = (requirement: RequirementWithDetails) => {
    setConfirmAction({ type: 'reject', requirementId: requirement.id, requirementTitle: requirement.title });
  };

  const handleDeleteRequirement = (requirement: RequirementWithDetails) => {
    console.log('Delete clicked for requirement:', requirement.id, requirement.title);
    setConfirmAction({ type: 'delete', requirementId: requirement.id, requirementTitle: requirement.title });
  };

  const handleConfirmAction = async () => {
    if (!confirmAction || !token || !projectId) return;

    try {
      if (confirmAction.type === 'approve') {
        await requirementService.approveRequirement(projectId, confirmAction.requirementId, token);
        setSuccess('Requirement approved successfully');
      } else if (confirmAction.type === 'reject') {
        await requirementService.rejectRequirement(projectId, confirmAction.requirementId, token);
        setSuccess('Requirement rejected successfully');
      } else if (confirmAction.type === 'delete') {
        await requirementService.deleteRequirement(projectId, confirmAction.requirementId, token);
        setSuccess('Requirement deleted successfully');
      }
      setConfirmAction(null);
      loadRequirements();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${confirmAction.type} requirement`);
      setConfirmAction(null);
    }
  };

  // Collaboration modal handlers
  const loadComments = async (requirementId: string) => {
    if (!token || !projectId) return;
    setIsLoadingComments(true);
    setCommentError('');
    try {
      const data = await commentService.getComments(projectId, requirementId, token);
      setComments(data);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setIsLoadingComments(false);
    }
  };

  const handleCollaborate = (requirement: RequirementWithDetails) => {
    setCollaboratingRequirement(requirement);
    loadComments(requirement.id);
  };

  const handleCloseCollaboration = () => {
    setCollaboratingRequirement(null);
    setComments([]);
    setNewCommentText('');
    setReplyingTo(null);
    setEditingComment(null);
    setEditCommentText('');
    setCommentError('');
  };

  const handlePostComment = async () => {
    if (!token || !projectId || !collaboratingRequirement || !newCommentText.trim()) {
      setCommentError('Comment text is required');
      return;
    }

    setCommentError('');
    try {
      const input: CreateCommentInput = {
        comment_text: newCommentText.trim(),
        parent_comment_id: replyingTo?.id
      };
      await commentService.createComment(projectId, collaboratingRequirement.id, input, token);
      setNewCommentText('');
      setReplyingTo(null);
      loadComments(collaboratingRequirement.id);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to post comment');
    }
  };

  const handleReply = (comment: RequirementComment) => {
    setReplyingTo(comment);
    setEditingComment(null);
  };

  const handleEditComment = (comment: RequirementComment) => {
    setEditingComment(comment);
    setEditCommentText(comment.comment_text);
    setReplyingTo(null);
  };

  const handleSaveEditComment = async () => {
    if (!token || !projectId || !collaboratingRequirement || !editingComment || !editCommentText.trim()) {
      setCommentError('Comment text is required');
      return;
    }

    setCommentError('');
    try {
      await commentService.updateComment(
        projectId,
        collaboratingRequirement.id,
        editingComment.id,
        { comment_text: editCommentText.trim() },
        token
      );
      setEditingComment(null);
      setEditCommentText('');
      loadComments(collaboratingRequirement.id);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!token || !projectId || !collaboratingRequirement) return;

    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await commentService.deleteComment(projectId, collaboratingRequirement.id, commentId, token);
      loadComments(collaboratingRequirement.id);
    } catch (err) {
      setCommentError(err instanceof Error ? err.message : 'Failed to delete comment');
    }
  };


  // Check if user can approve/reject requirements
  const canApproveReject = currentUser?.role === 'platform_admin' ||
                          currentUser?.role === 'company_admin' ||
                          currentUser?.role === 'project_manager';

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      critical: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
      high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400',
      medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
      low: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
    };
    return colors[priority] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400';
  };

  const getApprovalStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
      approved: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
      rejected: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400';
  };

  const getTypeColor = (type: string) => {
    // Use the same gradient for all requirement types (matches page header gradient)
    return 'bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400';
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, React.ReactElement> = {
      functional: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      'non-functional': (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      technical: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      business: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      'user-story': (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    };
    return icons[type] || (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  // Check if there's activity today
  const hasActivityToday = (latestCommentDate?: string | null) => {
    if (!latestCommentDate) return false;
    const commentDate = new Date(latestCommentDate);
    const today = new Date();
    return commentDate.toDateString() === today.toDateString();
  };

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent, requirementType: string) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Get current requirements of this type
    const typeReqs = groupedRequirements[requirementType];
    const oldIndex = typeReqs.findIndex((req) => req.id === active.id);
    const newIndex = typeReqs.findIndex((req) => req.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Optimistically update UI
    const newOrder = arrayMove(typeReqs, oldIndex, newIndex);
    setRequirements(prevReqs => {
      const updated = [...prevReqs];
      // Replace all requirements of this type with reordered ones
      const otherReqs = updated.filter(r => r.requirement_type !== requirementType || r.hierarchy_type !== 'parent');
      return [...otherReqs, ...newOrder].sort((a, b) => {
        // Maintain hierarchical order: parents before children
        if (a.hierarchy_type !== b.hierarchy_type) {
          return a.hierarchy_type === 'parent' ? -1 : 1;
        }
        return 0;
      });
    });

    // Persist to backend
    try {
      if (!token || !projectId) return;
      await requirementService.updateRequirementOrder(
        projectId,
        active.id as string,
        newIndex,
        token
      );
    } catch (err) {
      // Revert on error
      loadRequirements();
      setError(err instanceof Error ? err.message : 'Failed to update order');
    }
  };

  // Group requirements by type
  const requirementTypes = [
    { key: 'functional', label: 'Functional' },
    { key: 'non-functional', label: 'Non-Functional' },
    { key: 'technical', label: 'Technical' },
    { key: 'business', label: 'Business' },
    { key: 'user-story', label: 'User Story' }
  ];

  // Filter requirements based on search text and pending filter
  const filteredRequirements = requirements.filter(req => {
    // Apply search filter (search in title and description)
    const searchLower = searchText.toLowerCase();
    const matchesSearch = !searchText ||
      req.title.toLowerCase().includes(searchLower) ||
      (req.description && req.description.toLowerCase().includes(searchLower));

    // Apply pending filter
    const matchesPending = !showOnlyPending || req.approval_status === 'pending';

    // Apply with comments filter
    const matchesWithComments = !showWithComments || (req.comment_count && req.comment_count > 0);

    // Apply schedule health filter
    let matchesSchedule = true;
    if (scheduleFilter !== 'all') {
      if (scheduleFilter === 'overdue') {
        matchesSchedule = req.is_overdue === true;
      } else if (scheduleFilter === 'on-track') {
        matchesSchedule = req.is_on_schedule === true && !req.is_overdue;
      } else if (scheduleFilter === 'at-risk') {
        matchesSchedule = !req.is_on_schedule && !req.is_overdue && req.expected_end_date !== null;
      } else if (scheduleFilter === 'no-date') {
        matchesSchedule = !req.expected_end_date;
      }
    }

    return matchesSearch && matchesPending && matchesWithComments && matchesSchedule;
  });

  // Group requirements by type, but only include parent requirements in the main list
  // (children will be rendered nested under their parents)
  const groupedRequirements = requirementTypes.reduce((acc, type) => {
    acc[type.key] = filteredRequirements.filter(req =>
      req.requirement_type === type.key && req.hierarchy_type === 'parent'
    );
    return acc;
  }, {} as Record<string, RequirementWithDetails[]>);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-tertiary flex items-center justify-center">
        <div className="text-text-tertiary">Loading requirements...</div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
              >
                ← Back to Project
              </button>
              <h1 className="text-3xl font-bold text-white">Requirements</h1>
            </div>
            <button
              onClick={handleOpenAddModal}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            >
              + Add Requirement
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
            <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 rounded-md bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
            <p className="text-sm text-green-800 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Search and Filter Bar */}
        <div className="mb-6 bg-surface-primary rounded-lg shadow p-4">
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search in title and description..."
                className="block w-full pl-10 pr-3 py-2 border border-border-primary rounded-md leading-5 bg-surface-primary placeholder-gray-500 dark:placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 dark:placeholder-gray-500 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              />
              {searchText && (
                <button
                  onClick={() => setSearchText('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-tertiary hover:text-text-secondary"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Show Only Pending Toggle */}
            <div className="flex items-center">
              <button
                onClick={() => setShowOnlyPending(!showOnlyPending)}
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  showOnlyPending ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                    showOnlyPending ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="ml-3 text-sm font-medium text-text-secondary whitespace-nowrap">
                Show Only Pending
              </span>
            </div>

            {/* Show With Comments Toggle */}
            <div className="flex items-center">
              <button
                onClick={() => setShowWithComments(!showWithComments)}
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                  showWithComments ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                    showWithComments ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="ml-3 text-sm font-medium text-text-secondary whitespace-nowrap">
                Show With Comments
              </span>
            </div>

            {/* Schedule Health Filter */}
            <div className="flex items-center">
              <label className="text-sm font-medium text-text-secondary mr-2 whitespace-nowrap">
                Schedule:
              </label>
              <select
                value={scheduleFilter}
                onChange={(e) => setScheduleFilter(e.target.value as 'all' | 'overdue' | 'on-track' | 'at-risk' | 'no-date')}
                className="px-3 py-1.5 border border-border-primary rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All</option>
                <option value="overdue">Overdue</option>
                <option value="on-track">On Track</option>
                <option value="at-risk">At Risk</option>
                <option value="no-date">No Due Date</option>
              </select>
            </div>
          </div>

          {/* Active Filters Summary */}
          {(searchText || showOnlyPending || showWithComments || scheduleFilter !== 'all') && (
            <div className="mt-3 flex items-center gap-2 text-sm text-text-secondary">
              <span className="font-medium">Active filters:</span>
              {searchText && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Search: "{searchText}"
                  <button
                    onClick={() => setSearchText('')}
                    className="ml-1 inline-flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800"
                  >
                    ×
                  </button>
                </span>
              )}
              {showOnlyPending && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:text-yellow-400">
                  Pending Only
                  <button
                    onClick={() => setShowOnlyPending(false)}
                    className="ml-1 inline-flex items-center text-yellow-600 dark:text-yellow-400 hover:text-yellow-800 dark:text-yellow-400"
                  >
                    ×
                  </button>
                </span>
              )}
              {showWithComments && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:text-blue-400">
                  With Comments
                  <button
                    onClick={() => setShowWithComments(false)}
                    className="ml-1 inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-400"
                  >
                    ×
                  </button>
                </span>
              )}
              {scheduleFilter !== 'all' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:text-green-400">
                  Schedule: {scheduleFilter === 'overdue' ? 'Overdue' : scheduleFilter === 'on-track' ? 'On Track' : scheduleFilter === 'at-risk' ? 'At Risk' : 'No Due Date'}
                  <button
                    onClick={() => setScheduleFilter('all')}
                    className="ml-1 inline-flex items-center text-green-600 dark:text-green-400 hover:text-green-800 dark:text-green-400"
                  >
                    ×
                  </button>
                </span>
              )}
              <button
                onClick={() => {
                  setSearchText('');
                  setShowOnlyPending(false);
                  setShowWithComments(false);
                  setScheduleFilter('all');
                }}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 text-xs font-medium"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Requirements Swim Lanes */}
        {requirements.length === 0 ? (
          <div className="bg-surface-primary rounded-lg shadow p-8 text-center text-text-tertiary">
            <p>No requirements yet</p>
            <p className="text-sm mt-2">Click "Add Requirement" to create your first requirement</p>
          </div>
        ) : filteredRequirements.length === 0 ? (
          <div className="bg-surface-primary rounded-lg shadow p-8 text-center text-text-tertiary">
            <svg className="mx-auto h-12 w-12 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-4">No requirements match your filters</p>
            <p className="text-sm mt-2">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setSearchText('');
                setShowOnlyPending(false);
              }}
              className="mt-4 text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 text-sm font-medium"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {requirementTypes.map((type) => {
              const typeRequirements = groupedRequirements[type.key];
              if (typeRequirements.length === 0) return null;

              return (
                <div key={type.key} className={`bg-white rounded-lg shadow ${
                  typeRequirements.some(req => {
                    // Check if this requirement's dropdown is open
                    if (req.id === openDropdownId) return true;
                    // Check if any of its children's dropdown is open
                    const children = getChildren(req.id);
                    return children.some(child => child.id === openDropdownId);
                  }) ? 'overflow-visible' : 'overflow-hidden'
                }`}>
                  {/* Swim Lane Header */}
                  <div className={`${getTypeColor(type.key)} px-6 py-3 flex items-center`}>
                    <div className="mr-2 text-white">{getTypeIcon(type.key)}</div>
                    <h2 className="text-lg font-semibold text-white">{type.label}</h2>
                    <span className="ml-3 px-2 py-1 bg-surface-primary bg-opacity-20 rounded-full text-xs font-medium text-white">
                      {typeRequirements.length}
                    </span>
                  </div>

                  {/* Column Headers */}
                  <div className="bg-background-secondary border-b border-border-primary px-6 py-2 grid grid-cols-12 gap-4 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                    <div className="col-span-2">Title</div>
                    <div className="col-span-1">Priority</div>
                    <div className="col-span-1">Approval</div>
                    <div className="col-span-1">Due Date</div>
                    <div className="col-span-2">Created By</div>
                    <div className="col-span-1 text-center">Insight</div>
                    <div className="col-span-2 text-center">Discussion</div>
                    <div className="col-span-2 text-right">Actions</div>
                  </div>

                  {/* Requirements Rows with Drag-and-Drop */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => handleDragEnd(event, type.key)}
                  >
                    <SortableContext
                      items={typeRequirements.map(req => req.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="divide-y divide-gray-200">
                        {typeRequirements.map((req) => {
                          const children = getChildren(req.id);
                          const hasChildren = children.length > 0;
                          const isExpanded = !collapsedParents.has(req.id);

                          return (
                            <SortableRequirementRow
                              key={req.id}
                              req={req}
                              hasChildren={hasChildren}
                              isExpanded={isExpanded}
                              children={children}
                              onToggleCollapse={toggleParentCollapse}
                              onView={handleViewRequirement}
                              onEdit={handleEditRequirement}
                              onApprove={handleApproveRequirement}
                              onReject={handleRejectRequirement}
                              onDelete={handleDeleteRequirement}
                              onCollaborate={handleCollaborate}
                              canApproveReject={canApproveReject}
                              getPriorityColor={getPriorityColor}
                              getApprovalStatusColor={getApprovalStatusColor}
                              hasActivityToday={hasActivityToday}
                              openDropdownId={openDropdownId}
                              setOpenDropdownId={setOpenDropdownId}
                            />
                          );
                        })}
                      </div>
                    </SortableContext>
                  </DndContext>

                  {/* Quick-add requirement inline input */}
                  <div className="border-t border-border-primary px-6 py-3 bg-gray-50 dark:bg-gray-800">
                    {quickAddActiveSection === type.key ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={quickAddTitle}
                          onChange={(e) => setQuickAddTitle(e.target.value)}
                          onKeyDown={(e) => handleQuickAddKeyDown(e, type.key)}
                          onBlur={handleQuickAddCancel}
                          placeholder="Enter requirement title and press Enter..."
                          autoFocus
                          className="flex-1 px-3 py-2 border border-indigo-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={() => handleQuickAddActivate(type.key)}
                        className="flex items-center gap-2 text-sm text-text-secondary hover:text-indigo-600 dark:text-indigo-400 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Requirement
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Requirement Modal */}
      {isAddingRequirement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => { setIsAddingRequirement(false); setAddError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Header with gradient background */}
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-6 py-5 flex items-center justify-between">
                <h3 className="text-2xl font-bold text-white">Add Requirement</h3>
                <button
                  onClick={() => { setIsAddingRequirement(false); setAddError(''); }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-surface-primary p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                {/* Error Banner */}
                {addError && (
                  <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-red-800 dark:text-red-400">{addError}</p>
                  </div>
                )}

                {/* Basic Information Section */}
                <div className="bg-background-secondary rounded-lg p-5 border border-border-primary">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-text-primary">Basic Information</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Title *</label>
                      <input
                        type="text"
                        value={newRequirement.title}
                        onChange={(e) => setNewRequirement({ ...newRequirement, title: e.target.value })}
                        className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow"
                        placeholder="Enter requirement title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Description</label>
                      <textarea
                        value={newRequirement.description}
                        onChange={(e) => setNewRequirement({ ...newRequirement, description: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow"
                        placeholder="Enter requirement description"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Acceptance Criteria</label>
                      <textarea
                        value={newRequirement.acceptance_criteria || ''}
                        onChange={(e) => setNewRequirement({ ...newRequirement, acceptance_criteria: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow"
                        placeholder="Enter acceptance criteria"
                      />
                    </div>
                  </div>
                </div>

                {/* Classification Section */}
                <div className="bg-surface-primary rounded-lg p-5 border border-border-primary">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-text-primary">Classification</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Type</label>
                      <select
                        value={newRequirement.requirement_type}
                        onChange={(e) => setNewRequirement({ ...newRequirement, requirement_type: e.target.value as any })}
                        className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow"
                      >
                        <option value="functional">Functional</option>
                        <option value="non-functional">Non-Functional</option>
                        <option value="technical">Technical</option>
                        <option value="business">Business</option>
                        <option value="user-story">User Story</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Priority</label>
                      <select
                        value={newRequirement.priority}
                        onChange={(e) => setNewRequirement({ ...newRequirement, priority: e.target.value as any })}
                        className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Hierarchy Section */}
                <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-text-primary">Hierarchy</h4>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Hierarchy Type</label>
                      <select
                        value={newRequirement.hierarchy_type || 'parent'}
                        onChange={(e) => setNewRequirement({
                          ...newRequirement,
                          hierarchy_type: e.target.value as 'parent' | 'child',
                          parent_requirement_id: e.target.value === 'parent' ? undefined : newRequirement.parent_requirement_id
                        })}
                        className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow bg-surface-primary"
                      >
                        <option value="parent">Parent</option>
                        <option value="child">Child</option>
                      </select>
                    </div>
                    {newRequirement.hierarchy_type === 'child' && (
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Parent Requirement *</label>
                        {parentRequirements.length === 0 ? (
                          <div className="w-full px-4 py-2.5 border border-border-primary rounded-lg bg-background-secondary text-sm text-text-tertiary">
                            No parent requirements available. Please create a parent requirement first.
                          </div>
                        ) : (
                          <select
                            value={newRequirement.parent_requirement_id || ''}
                            onChange={(e) => setNewRequirement({ ...newRequirement, parent_requirement_id: e.target.value })}
                            className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-shadow bg-surface-primary"
                          >
                            <option value="">Select a parent requirement...</option>
                            {parentRequirements.map((parent) => (
                              <option key={parent.id} value={parent.id}>
                                {parent.title}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer with action buttons */}
              <div className="bg-background-secondary px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => { setIsAddingRequirement(false); setAddError(''); }}
                  className="inline-flex items-center px-5 py-2.5 border border-border-primary text-sm font-medium rounded-full text-text-secondary bg-surface-primary hover:bg-background-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddRequirement}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-transparent text-sm font-medium rounded-full text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 hover:from-indigo-700 hover:via-purple-700 hover:to-blue-700 transition-all shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Create Requirement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Requirement Modal */}
      {viewingRequirement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => setViewingRequirement(null)} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Purple Gradient Header */}
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">View Requirement</h3>
                  <button
                    onClick={() => setViewingRequirement(null)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-surface-primary px-6 py-6">
                <div className="space-y-6">
                  {/* Main Info Card */}
                  <div className="bg-background-secondary rounded-lg p-5 border border-border-primary">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-100">
                        <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h4 className="text-sm font-semibold text-text-primary">Basic Information</h4>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Title</label>
                        <p className="text-base font-medium text-text-primary">{viewingRequirement.title}</p>
                      </div>
                      {viewingRequirement.description && (
                        <div>
                          <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Description</label>
                          <p className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">{viewingRequirement.description}</p>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Acceptance Criteria</label>
                        {viewingRequirement.acceptance_criteria ? (
                          <p className="text-sm text-text-secondary whitespace-pre-wrap leading-relaxed">{viewingRequirement.acceptance_criteria}</p>
                        ) : (
                          <p className="text-sm text-text-tertiary italic">No acceptance criteria defined</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Classification & Status Section */}
                  <div className="bg-surface-primary rounded-lg p-5 border border-border-primary">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100">
                        <svg className="w-4 h-4 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      <h4 className="text-sm font-semibold text-text-primary">Classification & Status</h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary mb-1">Type</label>
                        <span className="text-sm font-medium text-text-primary capitalize">{viewingRequirement.requirement_type.replace('-', ' ')}</span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary mb-1">Priority</label>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold capitalize ${getPriorityColor(viewingRequirement.priority)}`}>
                          {viewingRequirement.priority}
                        </span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary mb-1">Approval Status</label>
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold capitalize ${getApprovalStatusColor(viewingRequirement.approval_status)}`}>
                          {viewingRequirement.approval_status}
                        </span>
                      </div>
                      {viewingRequirement.jira_status && (
                        <div>
                          <label className="block text-xs font-medium text-text-tertiary mb-1">JIRA Status</label>
                          <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:text-blue-400">
                            {viewingRequirement.jira_status}
                          </span>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary mb-1">Created By</label>
                        <p className="text-sm font-medium text-text-primary">{viewingRequirement.created_by_name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Assignment Section */}
                  {viewingRequirement.assigned_to_name && (
                    <div className="bg-surface-primary rounded-lg p-5 border border-border-primary">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100">
                          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <h4 className="text-sm font-semibold text-text-primary">Assignment</h4>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-text-tertiary mb-1">Assigned To</label>
                        <p className="text-sm font-medium text-text-primary">{viewingRequirement.assigned_to_name}</p>
                      </div>
                    </div>
                  )}

                  {/* AI-Generated Insights Section */}
                  {viewingRequirement.insights && (
                    <div className="mt-6 border-t border-purple-200 pt-4">
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <h4 className="text-sm font-semibold text-purple-900">AI Enhancement Insights</h4>
                        </div>
                        <div className="prose prose-sm prose-purple max-w-none">
                          <div
                            className="text-text-secondary leading-relaxed text-sm"
                            dangerouslySetInnerHTML={{
                              __html: parseMarkdown(viewingRequirement.insights)
                            }}
                          />
                        </div>
                        {(viewingRequirement.insights_generated_at || viewingRequirement.insights_model || viewingRequirement.insights_tokens_used) && (
                          <div className="mt-3 pt-3 border-t border-purple-200 flex flex-wrap gap-4 text-xs text-purple-700 dark:text-purple-400">
                            {viewingRequirement.insights_generated_at && (
                              <div>
                                <span className="font-medium">Generated:</span>{' '}
                                {new Date(viewingRequirement.insights_generated_at).toLocaleString()}
                              </div>
                            )}
                            {viewingRequirement.insights_model && (
                              <div>
                                <span className="font-medium">Model:</span> {viewingRequirement.insights_model}
                              </div>
                            )}
                            {viewingRequirement.insights_tokens_used && (
                              <div>
                                <span className="font-medium">Tokens:</span> {viewingRequirement.insights_tokens_used.toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timeline & Scheduling Section */}
                  {(viewingRequirement.expected_start_date || viewingRequirement.expected_end_date || viewingRequirement.actual_start_date || viewingRequirement.actual_end_date) && (
                    <div className="mt-6 border-t border-blue-200 pt-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <h4 className="text-sm font-semibold text-blue-900">Timeline & Scheduling</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {viewingRequirement.expected_start_date && (
                            <div>
                              <label className="block text-xs font-medium text-blue-700 mb-1">Expected Start</label>
                              <p className="text-sm text-text-primary">{new Date(viewingRequirement.expected_start_date).toLocaleDateString()}</p>
                            </div>
                          )}
                          {viewingRequirement.expected_end_date && (
                            <div>
                              <label className="block text-xs font-medium text-blue-700 mb-1">Expected End</label>
                              <p className="text-sm text-text-primary">{new Date(viewingRequirement.expected_end_date).toLocaleDateString()}</p>
                            </div>
                          )}
                          {viewingRequirement.expected_duration_days && (
                            <div>
                              <label className="block text-xs font-medium text-blue-700 mb-1">Expected Duration</label>
                              <p className="text-sm text-text-primary">{viewingRequirement.expected_duration_days} days</p>
                            </div>
                          )}
                          {viewingRequirement.actual_start_date && (
                            <div>
                              <label className="block text-xs font-medium text-green-700 mb-1">Actual Start</label>
                              <p className="text-sm text-text-primary">{new Date(viewingRequirement.actual_start_date).toLocaleDateString()}</p>
                            </div>
                          )}
                          {viewingRequirement.actual_end_date && (
                            <div>
                              <label className="block text-xs font-medium text-green-700 mb-1">Actual End</label>
                              <p className="text-sm text-text-primary">{new Date(viewingRequirement.actual_end_date).toLocaleDateString()}</p>
                            </div>
                          )}
                          {viewingRequirement.actual_duration_days && (
                            <div>
                              <label className="block text-xs font-medium text-green-700 mb-1">Actual Duration</label>
                              <p className="text-sm text-text-primary">{viewingRequirement.actual_duration_days} days</p>
                            </div>
                          )}
                        </div>
                        {(viewingRequirement.schedule_variance_days !== null && viewingRequirement.schedule_variance_days !== undefined) && (
                          <div className="mt-3 pt-3 border-t border-blue-200">
                            <div className="flex items-center gap-4">
                              <div>
                                <label className="block text-xs font-medium text-text-secondary mb-1">Schedule Variance</label>
                                <span className={`text-sm font-medium ${viewingRequirement.schedule_variance_days > 0 ? 'text-red-600 dark:text-red-400' : viewingRequirement.schedule_variance_days < 0 ? 'text-green-600 dark:text-green-400' : 'text-text-secondary'}`}>
                                  {viewingRequirement.schedule_variance_days > 0 ? '+' : ''}{viewingRequirement.schedule_variance_days} days
                                </span>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-text-secondary mb-1">Status</label>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${viewingRequirement.is_overdue ? 'bg-red-100 text-red-800 dark:text-red-400' : viewingRequirement.is_on_schedule ? 'bg-green-100 text-green-800 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:text-yellow-400'}`}>
                                  {viewingRequirement.is_overdue ? 'Overdue' : viewingRequirement.is_on_schedule ? 'On Schedule' : 'At Risk'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Workflow Dependencies Section */}
                  <div className="bg-surface-primary rounded-lg p-5 border border-border-primary">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100">
                        <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <h4 className="text-sm font-semibold text-text-primary">Workflow Dependencies</h4>
                    </div>
                    <DependencyManagement
                      projectId={projectId!}
                      requirementId={viewingRequirement.id}
                      requirementTitle={viewingRequirement.title}
                      availableRequirements={requirements}
                      onDependencyChange={loadRequirements}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-6 py-4 flex gap-3 justify-end border-t border-border-primary">
                <button
                  onClick={() => setViewingRequirement(null)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors shadow-sm"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setViewingRequirement(null);
                    handleEditRequirement(viewingRequirement);
                  }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 hover:from-indigo-700 hover:via-purple-700 hover:to-blue-700 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Requirement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Requirement Modal */}
      {editingRequirement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => { setEditingRequirement(null); setEditError(''); }} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Purple Gradient Header */}
              <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-white">Edit Requirement</h3>
                  <button
                    onClick={() => { setEditingRequirement(null); setEditError(''); }}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-surface-primary px-6 py-6">
                {editError && (
                  <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-300 flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium text-red-800 dark:text-red-400">{editError}</p>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Main Info Section */}
                  <div className="bg-background-secondary rounded-lg p-5 border border-border-primary">
                    <h4 className="text-sm font-semibold text-text-secondary mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Basic Information
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Title *</label>
                        <input
                          type="text"
                          value={editFormData.title}
                          onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                          className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
                          placeholder="Enter requirement title"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Description</label>
                        <textarea
                          value={editFormData.description}
                          onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow resize-none"
                          placeholder="Enter requirement description"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Acceptance Criteria</label>
                        <textarea
                          value={editFormData.acceptance_criteria || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, acceptance_criteria: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow resize-none"
                          placeholder="Enter acceptance criteria"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Classification & Status Section */}
                  <div className="bg-surface-primary rounded-lg p-5 border border-border-primary">
                    <h4 className="text-sm font-semibold text-text-secondary mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Classification & Status
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Type</label>
                        <select
                          value={editFormData.requirement_type}
                          onChange={(e) => setEditFormData({ ...editFormData, requirement_type: e.target.value as any })}
                          className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
                        >
                          <option value="functional">Functional</option>
                          <option value="non-functional">Non-Functional</option>
                          <option value="technical">Technical</option>
                          <option value="business">Business</option>
                          <option value="user-story">User Story</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Priority</label>
                        <select
                          value={editFormData.priority}
                          onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value as any })}
                          className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Approval Status</label>
                        <select
                          value={editFormData.approval_status}
                          onChange={(e) => setEditFormData({ ...editFormData, approval_status: e.target.value as any })}
                          className="w-full px-4 py-2.5 border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-shadow"
                        >
                          <option value="pending">Pending</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>
                      </div>
                      {editingRequirement.jira_status && (
                        <div>
                          <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">JIRA Status</label>
                          <div className="mt-2">
                            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:text-blue-400">
                              {editingRequirement.jira_status}
                            </span>
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-semibold text-text-tertiary uppercase tracking-wide mb-2">Created By</label>
                        <div className="mt-2">
                          <p className="text-sm font-medium text-text-primary">{editingRequirement.created_by_name}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timeline & Scheduling Section */}
                  <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
                    <h4 className="text-sm font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Timeline & Scheduling
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-blue-800 dark:text-blue-400 uppercase tracking-wide mb-2">Expected Start Date</label>
                        <input
                          type="date"
                          value={editFormData.expected_start_date || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, expected_start_date: e.target.value || null })}
                          className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-surface-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-blue-800 dark:text-blue-400 uppercase tracking-wide mb-2">Expected End Date</label>
                        <input
                          type="date"
                          value={editFormData.expected_end_date || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, expected_end_date: e.target.value || null })}
                          className="w-full px-4 py-2.5 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow bg-surface-primary"
                        />
                      </div>
                    </div>
                    {(editingRequirement.actual_start_date || editingRequirement.actual_end_date) && (
                      <div className="mt-4 p-4 bg-surface-primary rounded-lg border border-green-300">
                        <p className="text-xs text-green-800 dark:text-green-400 mb-3 font-semibold uppercase tracking-wide flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Actual Dates (Auto-tracked)
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {editingRequirement.actual_start_date && (
                            <div>
                              <span className="text-text-secondary">Started:</span> <span className="font-semibold text-text-primary">{new Date(editingRequirement.actual_start_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {editingRequirement.actual_end_date && (
                            <div>
                              <span className="text-text-secondary">Completed:</span> <span className="font-semibold text-text-primary">{new Date(editingRequirement.actual_end_date).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* AI-Generated Insights Section */}
                  {editingRequirement.insights && (
                    <div className="mt-6 border-t border-purple-200 pt-4">
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <div className="flex items-center gap-2 mb-3">
                          <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                          </svg>
                          <h4 className="text-sm font-semibold text-purple-900">AI Enhancement Insights</h4>
                        </div>
                        <div className="prose prose-sm prose-purple max-w-none">
                          <div
                            className="text-text-secondary leading-relaxed text-sm"
                            dangerouslySetInnerHTML={{
                              __html: parseMarkdown(editingRequirement.insights)
                            }}
                          />
                        </div>
                        {(editingRequirement.insights_generated_at || editingRequirement.insights_model || editingRequirement.insights_tokens_used) && (
                          <div className="mt-3 pt-3 border-t border-purple-200 flex flex-wrap gap-4 text-xs text-purple-700 dark:text-purple-400">
                            {editingRequirement.insights_generated_at && (
                              <div>
                                <span className="font-medium">Generated:</span>{' '}
                                {new Date(editingRequirement.insights_generated_at).toLocaleString()}
                              </div>
                            )}
                            {editingRequirement.insights_model && (
                              <div>
                                <span className="font-medium">Model:</span> {editingRequirement.insights_model}
                              </div>
                            )}
                            {editingRequirement.insights_tokens_used && (
                              <div>
                                <span className="font-medium">Tokens:</span> {editingRequirement.insights_tokens_used.toLocaleString()}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Workflow Dependencies Section */}
                <div className="bg-surface-primary rounded-lg p-5 border border-border-primary">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100">
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <h4 className="text-sm font-semibold text-text-primary">Workflow Dependencies</h4>
                  </div>
                  <DependencyManagement
                    projectId={projectId!}
                    requirementId={editingRequirement.id}
                    requirementTitle={editingRequirement.title}
                    availableRequirements={requirements}
                    onDependencyChange={loadRequirements}
                  />
                </div>
              </div>
              <div className="bg-background-secondary px-6 py-4 flex gap-3 justify-end border-t border-border-primary">
                <button
                  onClick={() => { setEditingRequirement(null); setEditError(''); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-surface-primary border border-border-primary rounded-full text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateRequirement}
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 hover:from-indigo-700 hover:via-purple-700 hover:to-blue-700 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={() => setConfirmAction(null)} />
            <div className="inline-block align-bottom bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-surface-primary px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${
                    confirmAction.type === 'approve' ? 'bg-green-100' : 'bg-red-100'
                  } sm:mx-0 sm:h-10 sm:w-10`}>
                    {confirmAction.type === 'approve' ? (
                      <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : confirmAction.type === 'delete' ? (
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    ) : (
                      <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-text-primary">
                      {confirmAction.type === 'approve'
                        ? 'Approve Requirement'
                        : confirmAction.type === 'delete'
                        ? 'Delete Requirement'
                        : 'Reject Requirement'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-text-tertiary">
                        {confirmAction.type === 'delete' ? (
                          <>
                            Are you sure you want to delete the requirement <span className="font-semibold">"{confirmAction.requirementTitle}"</span>? This action cannot be undone.
                          </>
                        ) : (
                          <>
                            Are you sure you want to {confirmAction.type} the requirement <span className="font-semibold">"{confirmAction.requirementTitle}"</span>? This action will update the requirement's approval status.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-background-secondary px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleConfirmAction}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                    confirmAction.type === 'approve'
                      ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                      : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  }`}
                >
                  {confirmAction.type === 'approve'
                    ? 'Approve'
                    : confirmAction.type === 'delete'
                    ? 'Delete'
                    : 'Reject'}
                </button>
                <button
                  onClick={() => setConfirmAction(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-full border border-border-primary shadow-sm px-4 py-2 bg-surface-primary text-base font-medium text-text-secondary hover:bg-background-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collaboration Modal */}
      {collaboratingRequirement && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-4">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75" onClick={handleCloseCollaboration} />
            <div className="relative bg-surface-primary rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:max-w-4xl sm:w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="bg-indigo-600 px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-white">Collaborate on Requirement</h3>
                </div>
                <button
                  onClick={handleCloseCollaboration}
                  className="text-white hover:text-gray-200 ml-4"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Requirement Summary */}
              <div className="border-b border-border-primary bg-background-secondary px-6 py-4">
                <h4 className="text-base font-semibold text-text-primary mb-2">{collaboratingRequirement.title}</h4>
                {collaboratingRequirement.description && (
                  <p className="text-sm text-text-secondary whitespace-pre-wrap">{collaboratingRequirement.description}</p>
                )}
              </div>

              {/* Comments Section */}
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                {isLoadingComments ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-text-tertiary">Loading comments...</div>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 text-text-tertiary">
                    <svg className="w-12 h-12 text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <p>No comments yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {comments.map((comment) => (
                      <CommentThread
                        key={comment.id}
                        comment={comment}
                        level={0}
                        currentUserId={currentUser?.id || ''}
                        onReply={handleReply}
                        onEdit={handleEditComment}
                        onDelete={handleDeleteComment}
                        editingComment={editingComment}
                        editCommentText={editCommentText}
                        setEditCommentText={setEditCommentText}
                        onSaveEdit={handleSaveEditComment}
                        onCancelEdit={() => setEditingComment(null)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* New Comment Section */}
              <div className="border-t border-border-primary bg-surface-primary px-6 py-4">
                {commentError && (
                  <div className="mb-3 p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-600 dark:text-red-400">{commentError}</p>
                  </div>
                )}
                {replyingTo && (
                  <div className="mb-3 p-3 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-md flex items-center justify-between">
                    <span className="text-sm text-indigo-700 dark:text-indigo-400">
                      Replying to <span className="font-semibold">@{replyingTo.user_name}</span>
                    </span>
                    <button
                      onClick={() => setReplyingTo(null)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                <textarea
                  value={newCommentText}
                  onChange={(e) => setNewCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={3}
                  maxLength={2000}
                />
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-text-tertiary">{newCommentText.length}/2000 characters</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => collaboratingRequirement && loadComments(collaboratingRequirement.id)}
                      disabled={isLoadingComments}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-surface-primary border border-border-primary rounded-md text-sm font-medium text-text-secondary hover:bg-background-secondary hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className={`w-4 h-4 ${isLoadingComments ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh Comments
                    </button>
                    <button
                      onClick={handlePostComment}
                      disabled={!newCommentText.trim()}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Post Comment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Assistant */}
      {projectId && (
        <>
          <ChatButton
            onClick={() => {
              console.log('🔵 ChatButton clicked. Current isChatOpen:', isChatOpen);
              console.log('🔵 handleCreateRequirementFromAI exists:', !!handleCreateRequirementFromAI);
              console.log('🔵 handleCreateRequirementFromAI type:', typeof handleCreateRequirementFromAI);
              setIsChatOpen(!isChatOpen);
            }}
            isOpen={isChatOpen}
          />
          <ChatWidget
            projectId={projectId}
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            onCreateRequirement={handleCreateRequirementFromAI}
          />
        </>
      )}
    </Layout>
  );
}

// Comment Thread Component (Recursive)
interface CommentThreadProps {
  comment: RequirementComment;
  level: number;
  currentUserId: string;
  onReply: (comment: RequirementComment) => void;
  onEdit: (comment: RequirementComment) => void;
  onDelete: (commentId: string) => void;
  editingComment: RequirementComment | null;
  editCommentText: string;
  setEditCommentText: (text: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
}

function CommentThread({
  comment,
  level,
  currentUserId,
  onReply,
  onEdit,
  onDelete,
  editingComment,
  editCommentText,
  setEditCommentText,
  onSaveEdit,
  onCancelEdit
}: CommentThreadProps) {
  const isEditing = editingComment?.id === comment.id;
  const isOwner = comment.user_id === currentUserId;

  return (
    <div className={`${level > 0 ? 'ml-8 mt-4 pl-4 border-l-2 border-indigo-200' : ''}`}>
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-medium">
            {commentService.getUserInitials(comment.user_first_name, comment.user_last_name)}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-text-primary">{comment.user_name}</span>
            <span className="text-xs text-text-tertiary">{commentService.formatTimestamp(comment.created_at)}</span>
            {comment.is_edited && (
              <span className="text-xs text-text-tertiary italic">(edited)</span>
            )}
          </div>
          {isEditing ? (
            <div className="mt-2">
              <textarea
                value={editCommentText}
                onChange={(e) => setEditCommentText(e.target.value)}
                className="w-full px-3 py-2 border border-border-primary rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows={2}
                maxLength={2000}
              />
              <div className="mt-2 flex items-center space-x-2">
                <button
                  onClick={onSaveEdit}
                  className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
                >
                  Save Changes
                </button>
                <button
                  onClick={onCancelEdit}
                  className="px-3 py-1 bg-gray-200 text-text-secondary text-sm rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {comment.is_deleted ? (
                <p className="mt-1 text-sm text-text-tertiary italic">[Comment deleted by user]</p>
              ) : (
                <p className="mt-1 text-sm text-text-secondary whitespace-pre-wrap">{comment.comment_text}</p>
              )}
              {!comment.is_deleted && (
                <div className="mt-2 flex items-center space-x-4">
                  <button
                    onClick={() => onReply(comment)}
                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 font-medium"
                  >
                    Reply
                  </button>
                  {isOwner && (
                    <>
                      <button
                        onClick={() => onEdit(comment)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:text-blue-400 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onDelete(comment.id)}
                        className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:text-red-400 font-medium"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
          {comment.replies && comment.replies.length > 0 && (
            <div className="mt-2">
              {comment.replies.map((reply) => (
                <CommentThread
                  key={reply.id}
                  comment={reply}
                  level={level + 1}
                  currentUserId={currentUserId}
                  onReply={onReply}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  editingComment={editingComment}
                  editCommentText={editCommentText}
                  setEditCommentText={setEditCommentText}
                  onSaveEdit={onSaveEdit}
                  onCancelEdit={onCancelEdit}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Requirements;
